import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from 'react';
import { Icon } from '../components/Icon.tsx';
import { t } from '../i18n/index.ts';

export interface Toast {
  readonly id: number;
  readonly tone: 'info' | 'success' | 'error';
  readonly message: string;
  readonly action?: { readonly label: string; readonly run: () => void };
}

interface ToastApi {
  push: (toast: Omit<Toast, 'id'>, ttlMs?: number) => void;
  dismiss: (id: number) => void;
}

const ToastContext = createContext<ToastApi | null>(null);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const nextId = useRef(1);
  const dismiss = useCallback((id: number) => setToasts((all) => all.filter((x) => x.id !== id)), []);
  const push = useCallback(
    (toast: Omit<Toast, 'id'>, ttlMs = toast.tone === 'error' ? 9000 : 4500) => {
      const id = nextId.current++;
      setToasts((all) => [...all.slice(-2), { ...toast, id }]);
      if (ttlMs > 0) window.setTimeout(() => dismiss(id), ttlMs);
    },
    [dismiss],
  );
  const api = useMemo(() => ({ push, dismiss }), [push, dismiss]);
  return (
    <ToastContext.Provider value={api}>
      {children}
      <div className="toasts" role="region" aria-label="Notifications">
        <div aria-live="polite" className="toasts__stack">
          {toasts.map((toast) => (
            <div key={toast.id} className={`toast toast--${toast.tone}`} role={toast.tone === 'error' ? 'alert' : 'status'}>
              <Icon name={toast.tone === 'error' ? 'alert' : 'check'} size={18} />
              <span className="toast__message">{toast.message}</span>
              {toast.action && (
                <button type="button" className="toast__action" onClick={() => { toast.action!.run(); dismiss(toast.id); }}>
                  {toast.action.label}
                </button>
              )}
              <button type="button" className="toast__close" aria-label={t('common.close')} onClick={() => dismiss(toast.id)}>
                <Icon name="close" size={14} />
              </button>
            </div>
          ))}
        </div>
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastApi {
  const api = useContext(ToastContext);
  if (!api) throw new Error('useToast outside ToastProvider');
  return api;
}
